import { Box, Step, StepLabel, Stepper } from '@mui/material'
import {
  JSX,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { layoutStyles } from '@/utils/theme'
import type { Device } from '@/pages/Device/Device'
import DeviceCalibrationSearchStep from '@/pages/calibration/DeviceCalibrationSearchStep'
import PreFlightStep from '@/pages/calibration/PreFlightStep'
import CalibrationTestStep from '@/pages/calibration/DeviceCalibrationTestStep'
import VerificationStep from '@/pages/calibration/VerificationStep'
import { Ba, ResistanceBox } from '@/pages/calibration/calibration'
import { Composition } from '@/pages/calibration/composition'
import CalibrationStep from '@/pages/calibration/calibrationStep/CalibrationStep'
import useWebSocket from '@/hooks/useWebSocket'
import config from '@/config/WebSocket'
import {
  RDI_3000,
  RSS_3000,
  RSS_3000_PC,
  RCS_3000,
} from '@/pages/Device/SupportedTypes'
import { getDeviceComposition } from '@/api/calibration'
import { getSupportedCountries } from '@/hooks/usePreFlightBase'
import useResponseValidator from '@/pages/utils/responseValidator'
import { CompositionSchema } from '@/pages/calibration/schema'

const PANELS = { SELECT: 0, PREFLIGHT: 1, CALIBRATION: 2, TEST: 3, VERIFY: 4 }

const CalibrationTestPage = (): JSX.Element => {
  const { t } = useTranslation(['app'])
  const { validate } = useResponseValidator()
  const [activeStep, setActiveStep] = useState(0) // logical step index
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null)
  const [composition, setComposition] = useState<Composition | null>(null)
  const [resistanceBox, setResistanceBox] = useState<ResistanceBox | null>(null)
  const [jumperBox, setJumperBox] = useState<string | null>(null)
  const [ba, setBa] = useState<Ba>('')
  const [dropOff, setDropOff] = useState('...')
  const [url, setUrl] = useState<string | null>(null)
  const { sendJsonMessage, lastJsonMessage } = useWebSocket(url)

  const isRdi = selectedDevice?.type === RDI_3000

  // rdi:     Select(0) PreFlight(1) Calibrate(2) Test(3) Verify(4)
  // non-rdi: Select(0) PreFlight(1) Test(2)              Verify(3)
  const stepLabels = useMemo(
    () => [
      { name: t('app:device.calibration.selectStep.name') },
      { name: t('app:device.calibration.preFlightStep.name') },
      ...(isRdi
        ? [
            {
              name: t('app:device.calibration.calibrateStep.name'),
              optional: !selectedDevice?.editable,
            },
          ]
        : []),
      { name: t('app:device.calibration.testStep.name') },
      { name: t('app:device.calibration.verifyStep.name') },
    ],
    [t, isRdi, selectedDevice?.editable],
  )

  // rdi has a 1:1 mapping. Non-rdi skips panel 2 (CalibrationStep), so
  // logical steps 2 and 3 map to physical panels 3 and 4.
  const logicalToPanel = useCallback(
    (logical: number): number => {
      if (isRdi) return logical
      const nonRdiMap: Record<number, number> = {
        0: PANELS.SELECT,
        1: PANELS.PREFLIGHT,
        2: PANELS.TEST,
        3: PANELS.VERIFY,
      }
      return nonRdiMap[logical] ?? logical
    },
    [isRdi],
  )

  const activePanelIndex = logicalToPanel(activeStep)

  // Children never know about step numbers - they just go forward or back.
  const goNext = useCallback(() => setActiveStep((s) => s + 1), [])
  const goBack = useCallback(() => setActiveStep((s) => s - 1), [])

  const handleStepChange = useCallback((logicalStep: number) => {
    if (logicalStep === 0) {
      setSelectedDevice(null)
      setComposition(null)
      setResistanceBox(null)
      setJumperBox(null)
      setBa('')
      setDropOff('...')
      setUrl(null)
    }
    setActiveStep(logicalStep)
  }, [])

  const startSelfTest = useCallback(() => {
    if (!selectedDevice) return
    let wsUrl = `${config.url}zkl-rc/${selectedDevice.uid}/test?t=${Date.now()}`
    switch (selectedDevice.type) {
      case RDI_3000:
        wsUrl = `${config.url}rdi/${selectedDevice.uid}/test?t=${Date.now()}`
        break
      case RSS_3000:
      case RSS_3000_PC:
        wsUrl = `${config.url}rss/${selectedDevice.uid}/test?t=${Date.now()}`
        break
      case RCS_3000:
        wsUrl = `${config.url}rcs/${selectedDevice.uid}/test?t=${Date.now()}`
        break
    }
    setUrl(wsUrl)
  }, [selectedDevice])

  const stopSelfTest = useCallback(() => setUrl(null), [])

  const prefetchComposition = useCallback(
    async (device: Device) => {
      const countries = getSupportedCountries(device.type)
      const country = countries.includes(device.country)
        ? device.country
        : (countries[0] ?? '')
      try {
        const response = await getDeviceComposition(device.uid, country)
        const validated = await validate<Composition>(
          CompositionSchema,
          response,
        )
        setComposition(validated)
      } catch {
        setComposition(null)
      }
    },
    [validate],
  )

  const handleDeviceSelected = useCallback(
    (device: Device) => {
      setSelectedDevice(device)
      void prefetchComposition(device)
    },
    [prefetchComposition],
  )

  useEffect(() => {
    return () => {
      setUrl(null)
    }
  }, [])

  const containerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    let node: HTMLElement | null = containerRef.current?.parentElement ?? null
    let scrollParent: HTMLElement | null = null
    while (node) {
      const { overflowY } = getComputedStyle(node)
      if (
        (overflowY === 'auto' || overflowY === 'scroll') &&
        node.scrollHeight > node.clientHeight
      ) {
        scrollParent = node
        break
      }
      node = node.parentElement
    }
    ;(scrollParent ?? window).scrollTo({ top: 0, behavior: 'smooth' })
  }, [activeStep])

  const panelRefs = useRef<(HTMLDivElement | null)[]>([])
  const [panelHeight, setPanelHeight] = useState<number>()

  useLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined
    const node = panelRefs.current[activePanelIndex]
    if (!node) return undefined
    const updateHeight = () => setPanelHeight(node.scrollHeight)
    updateHeight()
    const observer = new ResizeObserver(updateHeight)
    observer.observe(node)
    return () => observer.disconnect()
  }, [activePanelIndex])

  return (
    <Box
      ref={containerRef}
      sx={{ ...layoutStyles.pageContainer, mt: 0, p: 3 }}
      id="calibration-device-select-container"
    >
      <Box sx={{ width: '100%' }} id="stepper-box">
        <Stepper activeStep={activeStep} alternativeLabel>
          {stepLabels.map((label) => (
            <Step key={label.name}>
              <StepLabel>{label.name}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Box
          sx={{
            mt: 4,
            position: 'relative',
            overflow: 'hidden',
            height: panelHeight,
          }}
        >
          <Box
            sx={{
              transform: `translateX(-${activePanelIndex * 100}%)`,
              transition: 'transform 0.3s ease-in-out',
              display: 'flex',
              alignItems: 'flex-start',
            }}
          >
            <Box
              ref={(el: HTMLDivElement | null) => {
                panelRefs.current[0] = el
              }}
              sx={{ width: '100%', flexShrink: 0, overflow: 'hidden' }}
            >
              <DeviceCalibrationSearchStep
                setActiveStep={handleStepChange}
                onDeviceSelected={handleDeviceSelected}
              />
            </Box>
            <Box
              ref={(el: HTMLDivElement | null) => {
                panelRefs.current[1] = el
              }}
              sx={{ width: '100%', flexShrink: 0, overflow: 'hidden' }}
            >
              <PreFlightStep
                device={selectedDevice}
                setActiveStep={handleStepChange}
                onNext={goNext}
                composition={composition}
                setComposition={setComposition}
                resistanceBox={resistanceBox}
                setResistanceBox={setResistanceBox}
                jumperBox={jumperBox}
                setJumperBox={setJumperBox}
                ba={ba}
                setBa={setBa}
                dropOff={dropOff}
                setDropOff={setDropOff}
                isActive={activeStep === 1}
              />
            </Box>
            <Box
              ref={(el: HTMLDivElement | null) => {
                panelRefs.current[2] = el
              }}
              sx={{ width: '100%', flexShrink: 0, overflow: 'hidden' }}
            >
              {isRdi && selectedDevice && (
                <CalibrationStep
                  device={selectedDevice}
                  onBack={goBack}
                  onNext={goNext}
                />
              )}
            </Box>
            <Box
              ref={(el: HTMLDivElement | null) => {
                panelRefs.current[3] = el
              }}
              sx={{ width: '100%', flexShrink: 0, overflow: 'hidden' }}
            >
              <CalibrationTestStep
                device={selectedDevice}
                startSelfTest={startSelfTest}
                stopSelfTest={stopSelfTest}
                lastJsonMessage={lastJsonMessage}
                onBack={goBack}
                onNext={goNext}
              />
            </Box>
            <Box
              ref={(el: HTMLDivElement | null) => {
                panelRefs.current[4] = el
              }}
              sx={{ width: '100%', flexShrink: 0, overflow: 'hidden' }}
            >
              <VerificationStep
                device={selectedDevice}
                setActiveStep={handleStepChange}
                composition={composition}
                setComposition={setComposition}
                resistanceBox={resistanceBox}
                setResistanceBox={setResistanceBox}
                jumperBox={jumperBox}
                setJumperBox={setJumperBox}
                ba={ba}
                setBa={setBa}
                dropOff={dropOff}
                setDropOff={setDropOff}
                finishSelfTest={sendJsonMessage}
                lastJsonMessage={lastJsonMessage}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
export default CalibrationTestPage
